import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { DeviceTestEquipment } from "graphql/types"
import { useEffect, useState } from "react"

type AddTestEquipmentDialogProps = {
  open: boolean
  onClose: () => void
  formState: DeviceTestEquipment
  onSubmit: (arg: DeviceTestEquipment) => void
}

type ValidationErrors = {
  name?: string
  barcode?: string
}

export const AddTestEquipmentDialog = (props: AddTestEquipmentDialogProps) => {
  const { open, onClose, formState, onSubmit } = props

  const [localFormState, setLocalFormState] = useState<DeviceTestEquipment>(formState)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})

  useEffect(() => {
    setLocalFormState(formState)
    setValidationErrors({}) // Clear errors when form state changes
  }, [formState])

  const validateForm = (): ValidationErrors => {
    const errors: ValidationErrors = {}

    // Check required fields
    if (!localFormState.name?.trim()) {
      errors.name = "Bezeichnung ist erforderlich"
    }

    if (!localFormState.barcode?.trim()) {
      errors.barcode = "Barcode ist erforderlich"
    }

    return errors
  }

  const isFormValid = (): boolean => {
    const errors = validateForm()
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleFormChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target
    if (name) {
      const nextValue =
        name === "location" && typeof value === "string" && value ? value.toUpperCase() : value

      setLocalFormState((prevState) => ({ ...prevState, [name]: nextValue }))

      // Clear validation error for this field
      if (validationErrors[name as keyof ValidationErrors]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors[name as keyof ValidationErrors]
          return newErrors
        })
      }
    }
  }

  const handleSave = () => {
    if (isFormValid()) {
      onSubmit(localFormState)
    }
  }

  const handleClose = () => {
    setLocalFormState(formState)
    setValidationErrors({})
    onClose()
  }

  const hasValidationErrors = Object.keys(validationErrors).length > 0

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>
        {localFormState.id !== "NEW" ? "Testequipment bearbeiten" : "Testequipment hinzufügen"}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {hasValidationErrors && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
              Bitte korrigieren Sie die folgenden Fehler:
            </Typography>
            <Stack spacing={0.5}>
              {Object.entries(validationErrors).map(([field, error]) => (
                <Typography key={field} variant="body2">
                  • {error}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        <TextField
          size="small"
          name="name"
          label="Bezeichnung"
          type="text"
          fullWidth
          value={localFormState.name}
          onChange={handleFormChange}
          sx={{ mt: 1 }}
          required
          error={!!validationErrors.name}
          helperText={validationErrors.name}
        />

        <TextField
          size="small"
          name="barcode"
          label="Barcode"
          type="text"
          fullWidth
          value={localFormState.barcode}
          onChange={handleFormChange}
          required
          error={!!validationErrors.barcode}
          helperText={validationErrors.barcode}
        />

        <TextField
          size="small"
          name="description"
          label="Beschreibung"
          type="text"
          fullWidth
          value={localFormState.description}
          onChange={handleFormChange}
        />

        <TextField
          size="small"
          name="manufacturer"
          label="Hersteller"
          type="text"
          fullWidth
          value={localFormState.manufacturer}
          onChange={handleFormChange}
        />

        <TextField
          size="small"
          name="status"
          label="Status"
          type="text"
          fullWidth
          value={localFormState.status || ""}
          onChange={handleFormChange}
          helperText="Statusbezeichnung, z. B. Aktiv oder Archiviert"
        />

        <TextField
          size="small"
          name="inventoryNo"
          label="Inventarnummer"
          type="text"
          fullWidth
          value={localFormState.inventoryNo}
          onChange={handleFormChange}
        />

        <TextField
          size="small"
          name="calibrationDate"
          label="Kalibrierungsdatum"
          type="date"
          fullWidth
          value={localFormState.calibrationDate || ""}
          onChange={handleFormChange}
          InputLabelProps={{ shrink: true }}
          helperText="Datum der letzten Kalibrierung"
        />

        <TextField
          size="small"
          name="calibrationDue"
          label="Kalibrierung fällig"
          type="date"
          fullWidth
          value={localFormState.calibrationDue}
          onChange={handleFormChange}
          InputLabelProps={{ shrink: true }}
          helperText="Nächstes Kalibrierungsdatum"
        />

        <TextField
          size="small"
          select
          name="location"
          label="Standort"
          fullWidth
          value={localFormState.location || ""}
          onChange={handleFormChange}
          helperText="Bitte Hamburg oder Neumuenster auswählen"
        >
          <MenuItem value="">
            <em>Kein Standort</em>
          </MenuItem>
          <MenuItem value="HAMBURG">Hamburg</MenuItem>
          <MenuItem value="NEUMUENSTER">Neumünster</MenuItem>
        </TextField>

        <TextField
          size="small"
          name="uncertainty"
          label="Unsicherheit"
          type="text"
          fullWidth
          value={localFormState.uncertainty}
          onChange={handleFormChange}
        />

        <TextField
          size="small"
          name="teType"
          label="Typ"
          type="text"
          fullWidth
          value={localFormState.teType}
          onChange={handleFormChange}
        />
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" onClick={handleClose} />
        <StyledButton buttonType="save" onClick={handleSave} disabled={hasValidationErrors} />
      </DialogActions>
    </Dialog>
  )
}
